import React, { useEffect, useRef, useState } from "react";
import "./css/login.css";
import "./css/setting.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { checkSession, createSession } from "./common/session";

// ----------------------------------------------------------------
// [환경 감지] 앱인지 확인
// ----------------------------------------------------------------
const isAppEnvironment = () => {
    if (typeof window === "undefined") return false;
    if (window.ReactNativeWebView) return true;
    if (window.isNativeApp || window.__MY_CERT_USER_DATA__) return true;
    const ua = navigator.userAgent || "";
    return /wv/.test(ua) || (/Android/i.test(ua) && /Version\//.test(ua));
};

// ----------------------------------------------------------------
// [환경 감지] dev 도메인 여부
// ----------------------------------------------------------------
const isDevDomain = () => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname || "";
    return host.toLowerCase().includes("dev");
};

// ----------------------------------------------------------------
// [환경 감지] 모바일 여부
// ----------------------------------------------------------------
const isMobileDevice = () => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
        (window.innerWidth && window.innerWidth <= 768);
};


// [유틸] JWT 파싱
const parseJwt = (token) => {
    try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("JWT parse error", e);
        return null;
    }
};

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();

    const [authStep, setAuthStep] = useState("google-signup");
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [error, setError] = useState("");
    const [debugInfo, setDebugInfo] = useState("");

    const [googleReady, setGoogleReady] = useState(false);
    const googleClientRef = useRef(null);

    // 이름/이메일 표시용 상태
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");

    // 페이지 로드 시 세션 확인 (status 먼저 확인)
    useEffect(() => {
        const checkExistingSession = async () => {
            // API_BASE_URL 정의 (useEffect 내부에서 사용)
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

            // 웹 환경에서는 sessionStorage에 세션 토큰이 있을 때만 확인
            // (브라우저 탭을 닫으면 sessionStorage가 삭제되므로 웹에서는 항상 로그인 화면부터 시작)
            const isApp = isAppEnvironment();

            // RN 환경에서는 window.__MY_CERT_USER_DATA__가 있으면 handleNativeProfile이 처리하도록 대기
            if (isApp && window.__MY_CERT_USER_DATA__) {
                console.log("📱 RN 환경 - 로그인 데이터가 이미 주입됨, handleNativeProfile이 처리하도록 대기");
                return;
            }

            if (!isApp) {
                // 웹 환경: sessionStorage에 세션 토큰이 있을 때만 확인
                const sessionToken = sessionStorage.getItem('hb_session_token');
                if (!sessionToken) {
                    console.log("🌐 웹 환경 - 세션 토큰 없음, 로그인 화면 표시");
                    return;
                }
            }

            // RN 환경이거나 웹에서 세션 토큰이 있는 경우만 세션 확인
            const sessionResult = await checkSession();

            // status 확인: active일 때만 세션 유효로 판단
            if (sessionResult.isValid && sessionResult.status === 'active') {
                // 세션이 있고 status가 active면 비밀번호 페이지로 이동
                console.log("✅ 세션 확인됨 (status: active) - 비밀번호 페이지로 이동");
                navigate("/password", { replace: true });
            } else if (sessionResult.status && sessionResult.status !== 'active') {
                // status가 active가 아니면 안내 페이지로 이동
                console.log(`⏸️ 사용자 status: ${sessionResult.status} - 안내 페이지로 이동`);
                // status에 따라 안내 메시지 설정
                setUserStatus(sessionResult.status);
                setShowStatusMessage(true);
                if (sessionResult.status === 'wait') {
                    setStatusMessage("승인 대기 중입니다.\n관리자 승인 후 이용 가능합니다.");
                } else if (sessionResult.status === 'reject') {
                    setStatusMessage("가입이 거절되었습니다.\n관리자에게 문의해주세요.");
                } else if (sessionResult.status === 'hold') {
                    setStatusMessage("계정이 보류되었습니다.\n관리자에게 문의해주세요.");
                } else {
                    setStatusMessage(`현재 상태(${sessionResult.status})에서는 이용할 수 없습니다.`);
                }
            } else if (isApp && !sessionResult.isValid) {
                // RN 환경에서 세션이 없고 window.__MY_CERT_USER_DATA__도 없을 때만 이메일로 사용자 확인
                // (window.__MY_CERT_USER_DATA__가 있으면 handleNativeProfile이 처리)
                if (window.__MY_CERT_USER_DATA__) {
                    console.log("📱 RN 환경 - 로그인 데이터가 주입됨, handleNativeProfile이 처리하도록 대기");
                    return;
                }

                const savedEmail = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
                if (savedEmail) {
                    // 이메일이 있으면 사용자 정보 확인
                    try {
                        const userCheckRes = await fetch(`${API_BASE_URL}/auth/user-by-email?email=${encodeURIComponent(savedEmail)}`, {
                            method: "GET",
                        });

                        if (!userCheckRes.ok || userCheckRes.status === 404) {
                            // 사용자가 없으면 회원가입 필요
                            console.log("📝 [회원가입 필요] 사용자 없음 (404) - 회원가입 안내로 이동");
                            setSignupStep("confirm");
                            setUserEmail(savedEmail);
                            const savedName = localStorage.getItem("hb_googleName") || "";
                            if (savedName) setUserName(savedName);

                            // 회원가입 폼 초기화
                            setSignupForm({
                                name: "",
                                phone: "",
                                birthdayYear: "",
                                birthdayMonth: "",
                                birthdayDay: "",
                                message: "",
                                level: "1",
                                position: "",
                                yearAssociation: "",
                                localAssociation: "",
                                church: "",
                            });
                            return;
                        }

                        const userData = await userCheckRes.json();
                        // userData.user가 null이거나 undefined이면 회원가입 필요
                        if (!userData || !userData.user || userData.user === null) {
                            // 사용자 정보가 없으면 회원가입 필요
                            console.log("📝 [회원가입 필요] 사용자 정보 없음 - 회원가입 안내로 이동");
                            setSignupStep("confirm");
                            setUserEmail(savedEmail);
                            const savedName = localStorage.getItem("hb_googleName") || "";
                            if (savedName) setUserName(savedName);

                            // 회원가입 폼 초기화
                            setSignupForm({
                                name: "",
                                phone: "",
                                birthdayYear: "",
                                birthdayMonth: "",
                                birthdayDay: "",
                                message: "",
                                level: "1",
                                position: "",
                                yearAssociation: "",
                                localAssociation: "",
                                church: "",
                            });
                            return;
                        }

                        // 사용자가 있으면 status 확인
                        const userStatus = userData.user.status || null;
                        if (userStatus === 'active') {
                            // active면 비밀번호 페이지로 이동
                            console.log("✅ 사용자 status: active - 비밀번호 페이지로 이동");
                            navigate("/password", { replace: true });
                        } else if (userStatus) {
                            // status가 있으면 안내 페이지 표시
                            console.log(`⏸️ 사용자 status: ${userStatus} - 안내 페이지로 이동`);
                            setUserStatus(userStatus);
                            setShowStatusMessage(true);
                            if (userStatus === 'wait') {
                                setStatusMessage("승인 대기 중입니다.\n관리자 승인 후 이용 가능합니다.");
                            } else if (userStatus === 'reject') {
                                setStatusMessage("가입이 거절되었습니다.\n관리자에게 문의해주세요.");
                            } else if (userStatus === 'hold') {
                                setStatusMessage("계정이 보류되었습니다.\n관리자에게 문의해주세요.");
                            } else {
                                setStatusMessage(`현재 상태(${userStatus})에서는 이용할 수 없습니다.`);
                            }
                        }
                    } catch (checkError) {
                        console.error("❌ 사용자 확인 중 오류:", checkError);
                        // 오류 발생 시 로그인 화면 표시
                    }
                } else {
                    // 이메일도 없으면 로그인 화면 표시 (RN에서 데이터 주입 대기)
                    console.log("📱 RN 환경 - 세션 및 이메일 없음, 로그인 화면 표시 (RN 데이터 주입 대기)");
                    // RN에서 로그인 데이터를 주입하면 handleNativeProfile이 호출됨
                }
            }
        };

        checkExistingSession();
    }, [navigate]);

    // 회원가입 단계 및 폼 상태
    // signupStep: null | 'confirm' | 'phone' | 'message'
    const [signupStep, setSignupStep] = useState(null);
    const [signupForm, setSignupForm] = useState({
        name: "", // 빈칸으로 초기화 (구글 이름은 화면에 표시하지 않음)
        phone: "",
        birthdayYear: "",
        birthdayMonth: "",
        birthdayDay: "",
        message: "",
        level: "1", // 등급: 1(정회원), 2(준회원)
        position: "", // 직책
        yearAssociation: "", // 연회
        localAssociation: "", // 지방회
        church: "", // 교회
    });

    // 관리자 문의하기 모달 상태
    const [showAdminContactModal, setShowAdminContactModal] = useState(false);
    const adminPhoneNumber = "02-1599-1873";

    // 회원가입 성공 화면 상태
    const [showSignupSuccess, setShowSignupSuccess] = useState(false);

    // 로그인 시 받은 사용자 상태 저장
    const [userStatus, setUserStatus] = useState(null);
    const [userEmailForActivate, setUserEmailForActivate] = useState("");

    // 상태별 안내 페이지 표시 여부
    const [showStatusMessage, setShowStatusMessage] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    // 로딩 스피너 상태
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");

    const devMode = isDevDomain();
    const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

    // 마지막으로 받은 구글 토큰/프로필 (회원가입 시 재사용)
    const lastTokenRef = useRef({ idToken: null, accessToken: null });
    const lastProfileRef = useRef(null);

    // ----------------------------------------------------------------
    // [공통] 백엔드 구글 인증 호출
    //  - 앱: idToken 사용 (+ profileName/profilePicture/phone)
    //  - 웹: accessToken 사용 (+ profileName/profilePicture/phone)
    //  - 반환: { ok, mode, user?, token?, status?, diff? } 또는 null
    // ----------------------------------------------------------------
    const callAuthGoogle = async ({
        idToken,
        accessToken,
        profileName,
        profilePicture,
        phone,
    }) => {
        console.log("🔑 [callAuthGoogle] 함수 호출됨");
        console.log("   - API URL:", `${API_BASE_URL}/auth/google`);
        console.log("   - 전송 데이터:", {
            hasIdToken: !!idToken,
            idTokenLength: idToken ? idToken.length : 0,
            hasAccessToken: !!accessToken,
            profileName: profileName || "없음",
            hasProfilePicture: !!profilePicture,
            phone: phone || "없음"
        });

        try {
            const requestBody = {};
            if (idToken) requestBody.idToken = idToken;
            if (accessToken) requestBody.accessToken = accessToken;
            if (profileName) requestBody.profileName = profileName;
            if (profilePicture) requestBody.profilePicture = profilePicture;
            if (phone) requestBody.phone = phone;
            console.log("   - 요청 Body:", JSON.stringify(requestBody, null, 2));

            const res = await fetch(`${API_BASE_URL}/auth/google`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            console.log("   - 응답 Status:", res.status, res.statusText);

            const data = await res.json();
            console.log("   - 응답 Data:", JSON.stringify(data, null, 2));

            console.log("✅ [callAuthGoogle] 응답 요약:", {
                ok: data.ok,
                mode: data.mode,
                status: data.status || data.user?.status,
                hasToken: !!data.token,
                hasUser: !!data.user,
                userId: data.user?.id,
                userEmail: data.user?.email
            });

            if (!res.ok) {
                console.error("❌ [callAuthGoogle] HTTP 오류:", res.status);
                console.error("   - Error data:", JSON.stringify(data, null, 2));
                // HTTP 오류도 적절한 응답 객체로 반환
                return {
                    ok: false,
                    mode: "error",
                    message: data.message || "백엔드 로그인에 실패했습니다.",
                    status: res.status
                };
            }

            // ok === true 인 경우에만 토큰/유저 정보 저장 및 세션 생성
            if (data.ok && data.token && data.user) {
                console.log("💾 [callAuthGoogle] localStorage 저장 - ok=true, token 있음");
                localStorage.setItem("hb_backendToken", data.token);
                localStorage.setItem("hb_backendUserId", String(data.user.id));
                if (data.user.email) {
                    localStorage.setItem("hb_serverEmail", data.user.email);
                    localStorage.setItem("hb_googleEmail", data.user.email);
                }
                if (data.user.name) {
                    localStorage.setItem("hb_googleName", data.user.name);
                    localStorage.setItem("hb_serverName", data.user.name);
                }
                if (data.user.picture) {
                    localStorage.setItem("hb_googlePicture", data.user.picture);
                }

                // 세션 생성 (MySQL에 저장)
                const sessionToken = await createSession(data.user.id);
                if (sessionToken) {
                    console.log("✅ 세션 생성 완료");
                } else {
                    console.error("❌ 세션 생성 실패");
                }

                console.log("   - 저장된 정보:", {
                    hasToken: !!localStorage.getItem("hb_backendToken"),
                    userId: localStorage.getItem("hb_backendUserId"),
                    email: localStorage.getItem("hb_serverEmail"),
                    name: localStorage.getItem("hb_serverName"),
                    hasSession: !!sessionToken
                });
            } else if (data.user) {
                console.log("💾 [callAuthGoogle] localStorage 저장 - user 정보만 있음");
                // 로그인은 아니더라도 서버에 있는 이메일/이름 정보를 저장
                if (data.user.email) {
                    localStorage.setItem("hb_serverEmail", data.user.email);
                    localStorage.setItem("hb_googleEmail", data.user.email);
                }
                if (data.user.name) {
                    localStorage.setItem("hb_serverName", data.user.name);
                    localStorage.setItem("hb_googleName", data.user.name);
                }
                if (data.user.picture) {
                    localStorage.setItem("hb_googlePicture", data.user.picture);
                }
            } else if (data.mode === "pending" && data.status === "ready") {
                console.log("💾 [callAuthGoogle] localStorage 저장 - ready 상태, 프로필 정보만");
                // ready 상태일 때 data.user가 없어도 구글 프로필 정보를 저장
                // (프로필 정보는 callAuthGoogle 호출 시 전달된 profileName, profilePicture 사용)
                if (profileName) {
                    localStorage.setItem("hb_googleName", profileName);
                    localStorage.setItem("hb_serverName", profileName);
                }
                if (profilePicture) {
                    localStorage.setItem("hb_googlePicture", profilePicture);
                }
            }

            console.log("✅ [callAuthGoogle] 완료, 반환 데이터:", {
                ok: data.ok,
                mode: data.mode,
                status: data.status || data.user?.status
            });
            return data;
        } catch (e) {
            console.error("❌ [callAuthGoogle] 예외 발생:", e);
            console.error("   - Error name:", e?.name);
            console.error("   - Error message:", e?.message);
            console.error("   - Error stack:", e?.stack);
            // 네트워크 오류나 예외 발생 시에도 적절한 응답 객체 반환
            return {
                ok: false,
                mode: "network_error",
                message: "서버 연결 중 오류가 발생했습니다.\n네트워크를 확인해주세요.",
            };
        }
    };

    // 휴대폰/유선 전화번호 자동 하이픈 포맷 (setting.js와 동일)
    const formatPhoneKOR = (input = "") => {
        const v = String(input).replace(/\D/g, "");
        if (!v) return "";
        if (v.startsWith("02")) {
            if (v.length <= 2) return v;
            if (v.length <= 5) return `02-${v.slice(2)}`;
            if (v.length <= 9) return `02-${v.slice(2, 5)}-${v.slice(5)}`;
            return `02-${v.slice(2, 6)}-${v.slice(6, 10)}`;
        }
        const head3 = v.slice(0, 3);
        if (v.length <= 3) return head3;
        if (v.length <= 7) return `${head3}-${v.slice(3)}`;
        if (v.length <= 10) return `${head3}-${v.slice(3, 6)}-${v.slice(6, 10)}`;
        return `${head3}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
    };

    // ----------------------------------------------------------------
    // 공통 1초 로딩 스피너
    // ----------------------------------------------------------------
    const showSpinnerFor = (message, duration = 1000) =>
        new Promise((resolve) => {
            setLoadingMessage(message || "");
            setLoading(true);
            setTimeout(() => {
                setLoading(false);
                setLoadingMessage("");
                resolve();
            }, duration);
        });

    // ----------------------------------------------------------------
    // 회원가입 제출
    //  - lastTokenRef / lastProfileRef 에 저장된 구글 정보 기반으로 /auth/register 호출
    // ----------------------------------------------------------------
    const handleSignupSubmit = async () => {
        if (!signupForm.name || signupForm.name.trim() === "" || !signupForm.phone) {
            setError("기본정보를 입력해주세요.");
            return;
        }

        const { idToken, accessToken } = lastTokenRef.current || {};
        const profile = lastProfileRef.current || {};

        if (!idToken && !accessToken) {
            setError("구글 인증 정보가 없습니다. 다시 로그인해 주세요.");
            return;
        }

        // 휴대폰 번호에서 숫자만 추출 (하이픈 제거)
        const phoneDigitsOnly = signupForm.phone.replace(/\D/g, "");

        // dev 모드 체크
        const isDevMode = devMode || (accessToken && accessToken.startsWith('dev-'));

        try {
            // birthday 처리: 년, 월, 일을 YYYY-MM-DD 형식으로 조합
            let birthdayValue = null;
            if (signupForm.birthdayYear && signupForm.birthdayMonth && signupForm.birthdayDay) {
                // YYYY-MM-DD 형식으로 조합 (DATE 형식과 일치)
                birthdayValue = `${signupForm.birthdayYear}-${signupForm.birthdayMonth}-${signupForm.birthdayDay}`;
                console.log("회원가입: 생년월일 전송:", birthdayValue);
            } else {
                console.log("회원가입: 생년월일이 완전하지 않아서 null로 전송");
            }

            const requestBody = {
                name: signupForm.name || "", // 사용자가 입력한 이름
                phone: phoneDigitsOnly, // 숫자만 전송
                birthday: birthdayValue,
                message: signupForm.message,
                profilePicture: profile.picture || null,
                profileName: profile.name || null, // 구글 이름 (백엔드에서 google_name으로 저장)
                level: signupForm.level || "1", // 등급 (기본값: 1)
                position: signupForm.position || null, // 직책
                yearAssociation: signupForm.yearAssociation || null, // 연회
                localAssociation: signupForm.localAssociation || null, // 지방회
                church: signupForm.church || null, // 교회
                status: "active", // 요청에 따라 가입 시 바로 active 처리
            };

            console.log("회원가입: 서버로 전송할 데이터:", JSON.stringify(requestBody, null, 2));

            if (isDevMode) {
                // dev 모드: 기본 이메일 사용 (입력한 이메일)
                requestBody.accessToken = accessToken || "dev-access-token";
                requestBody.email = userEmail || profile.email; // userEmail 우선 사용
            } else {
                // 운영 모드: 구글 로그인 토큰 전송 (이메일, 이름, 핸드폰은 구글에서 가져옴)
                if (idToken) requestBody.idToken = idToken;
                if (accessToken) requestBody.accessToken = accessToken;
            }

            const res = await fetch(`${API_BASE_URL}/auth/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            const data = await res.json();
            if (!res.ok || !data.ok) {
                const errorMsg = data.message || data.error || "기본정보 입력 중 오류가 발생했습니다.";
                console.error("Register error response:", data);
                setError(errorMsg);
                return;
            }

            // 1초 동안 로딩 스피너 표시
            await showSpinnerFor("");

            // 성공 화면 표시
            setShowSignupSuccess(true);
            setSignupStep(null);
            setError("");
        } catch (e) {
            console.error("register error", e);
            setError("기본정보 입력 중 오류가 발생했습니다.");
        }
    };

    // ----------------------------------------------------------------
    // 활성화하기 버튼 클릭 시 status를 active로 변경
    // ----------------------------------------------------------------
    const handleActivate = async () => {
        console.log("활성화 실행")
        try {
            const email = userEmailForActivate || userEmail;
            if (!email) {
                setError("이메일 정보가 없습니다. 다시 로그인해 주세요.");
                return;
            }

            // 1초 동안 로딩 스피너 표시
            await showSpinnerFor("활성화 중입니다...");

            // ready 상태일 때는 이메일로 활성화 (토큰 없이)
            const res = await fetch(`${API_BASE_URL}/auth/activate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: email,
                }),
            });

            const data = await res.json();
            if (!res.ok || !data.ok) {
                setError(data.message || "활성화 중 오류가 발생했습니다.");
                setShowStatusMessage(false);
                setStatusMessage("");
                return;
            }

            // 활성화 성공 - 사용자 ID가 있으면 세션 생성
            if (data.userId) {
                console.log("✅ 활성화 성공 - 사용자 ID:", data.userId);

                // 이메일 정보 저장 (세션 생성에 필요)
                if (email) {
                    localStorage.setItem("hb_serverEmail", email);
                    localStorage.setItem("hb_googleEmail", email);
                }

                // 🔐 활성화 시점에 세션 생성 (MySQL 세션)
                let sessionToken = null;
                try {
                    sessionToken = await createSession(data.userId);
                    if (sessionToken) {
                        console.log("✅ [활성화] 세션 생성 완료:", {
                            userId: data.userId,
                            hasSessionToken: !!sessionToken,
                            sessionToken: sessionToken.substring(0, 16) + '...'
                        });
                    } else {
                        console.warn("⚠️ [활성화] 세션 생성 실패 (sessionToken 없음)");
                    }
                } catch (e) {
                    console.error("❌ [활성화] 세션 생성 중 오류:", e);
                    // 세션 생성 실패해도 활성화 자체는 성공했으므로 진행 (단, 이후 웹에서는 다시 로그인 요구될 수 있음)
                }

                // 활성화 성공 후 상태 업데이트
                setUserStatus("active");
                setError("");
                setShowStatusMessage(false);
                setStatusMessage("");

                // 세션 생성이 완료된 후 비밀번호 페이지로 이동
                console.log("✅ 활성화 완료 - 세션 생성 후 비밀번호 페이지로 이동");
                navigate("/password", {
                    replace: true,
                    state: {
                        userStatus: "active",
                        isAfterActivate: true // 활성화 직후임을 명시
                    }
                });
            } else {
                // 사용자 ID가 없으면 에러 표시
                setError("활성화는 되었지만 사용자 정보를 가져올 수 없습니다. 다시 로그인해 주세요.");
                setShowStatusMessage(false);
                setStatusMessage("");
                return;
            }
        } catch (e) {
            console.error("Activate error:", e);
            setError("활성화 중 오류가 발생했습니다.");
            setShowStatusMessage(false);
            setStatusMessage("");
        }
    };

    // ----------------------------------------------------------------
    // [DEV 전용] 목업 로그인 정보를 백엔드에 저장
    //  - 실제 구글 인증 없이 email/name 을 users 테이블에 upsert
    //  - 반환값: { user, isNewUser } 또는 undefined
    // ----------------------------------------------------------------
    const syncDevLoginToBackend = async ({ email, name }) => {
        try {
            const res = await fetch(`${API_BASE_URL}/auth/dev-login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email,
                    name,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                console.error("Backend dev-login error:", data);
                setError(data.message || "DEV 로그인(목업) 저장에 실패했습니다.");
                return null;
            }

            // 백엔드 응답에서 ok, mode 확인 (실제 로그인과 동일한 구조)
            if (data.ok && data.token && data.user) {
                // 로그인 성공
                localStorage.setItem("hb_backendToken", data.token);
                localStorage.setItem("hb_backendUserId", String(data.user.id));
                if (data.user.email) {
                    localStorage.setItem("hb_serverEmail", data.user.email);
                }
                if (data.user.name) {
                    localStorage.setItem("hb_serverName", data.user.name);
                }
                if (data.user.grade != null) {
                    localStorage.setItem("hb_userGrade", String(data.user.grade));
                } else if (!localStorage.getItem("hb_userGrade")) {
                    localStorage.setItem("hb_userGrade", "3");
                }
            } else if (data.user) {
                // 로그인은 아니더라도 서버에 있는 이메일/이름 정보를 저장
                if (data.user.email) {
                    localStorage.setItem("hb_serverEmail", data.user.email);
                }
                if (data.user.name) {
                    localStorage.setItem("hb_serverName", data.user.name);
                }
            }

            return data;
        } catch (e) {
            console.error("Backend dev-login error:", e);
            setError("DEV 로그인(목업) 저장 중 오류가 발생했습니다.");
            return null;
        }
    };


    // ----------------------------------------------------------------
    // [공통 로직] 로그인 성공 후 -> OTP 단계 결정
    //  - isNewUser === true  → 비밀번호 설정/확인 절차
    //  - isNewUser === false → 비밀번호 입력 절차
    //  - 둘 다 아니면(local 기준) 기존 로직으로 fallback
    // ----------------------------------------------------------------
    const processLoginLogic = (newEmail, idToken, newName, isNewUser) => {
        const prevEmail = localStorage.getItem("hb_googleEmail");

        console.log(
            `계정 확인 - 기존: ${prevEmail}, 신규: ${newEmail}, 서버 isNewUser: ${isNewUser}`
        );

        // 현재 로그인 정보 저장
        localStorage.setItem("hb_googleEmail", newEmail || "");
        if (idToken) localStorage.setItem("hb_googleAccessToken", idToken);
        localStorage.setItem("hb_hasGoogleAccount", "true");
        localStorage.setItem("hb_isLoggedIn", "true");

        // 이름 저장
        if (newName) {
            localStorage.setItem("hb_googleName", newName);
        }

        // 상태 반영 (서버에서 받은 이름/이메일을 우선 사용)
        setUserEmail(newEmail || "");
        setUserName(newName || localStorage.getItem("hb_googleName") || "");

        // 비밀번호 페이지로 이동 (신규 사용자는 회원가입 안내로 이동)
        console.log("로그인 성공 - 비밀번호 페이지로 이동");
        navigate("/password", {
            replace: true,
            state: {
                userStatus: null // 필요시 상태 전달
            }
        });
    };

    // ----------------------------------------------------------------
    // 1. 네이티브(앱) 데이터 처리
    // ----------------------------------------------------------------
    const handleNativeProfile = async (profile) => {
        console.log("handleNativeProfile 호출됨, 수신 데이터:", profile);

        try {
            if (!profile) {
                console.error("handleNativeProfile: profile이 null 또는 undefined입니다.");
                setError("로그인 정보를 받지 못했습니다.");
                setAuthStep(null);
                return;
            }

            // RN에서 넘어오는 데이터 형식 처리 (idToken 또는 email 직접 전달)
            let email, name, picture, idToken;

            if (profile.idToken) {
                // idToken이 있는 경우 (기존 방식)
                idToken = profile.idToken;
                const payload = parseJwt(idToken);
                if (!payload) {
                    console.error("handleNativeProfile: 토큰 파싱 실패");
                    setError("토큰 형식이 올바르지 않습니다.");
                    setAuthStep(null);
                    return;
                }
                email = payload.email;
                name = payload.name;
                picture = payload.picture;
            } else if (profile.email) {
                // email이 직접 전달된 경우 (RN에서 email만 전달)
                email = profile.email;
                name = profile.name || "";
                picture = profile.picture || null;
                idToken = null;
                console.log("handleNativeProfile: email 직접 전달됨:", email);
            } else {
                const debugMsg =
                    "로그인 데이터 형식 오류. 수신 데이터:\n" + JSON.stringify(profile, null, 2);
                console.error("handleNativeProfile: idToken 또는 email이 없습니다.", debugMsg);
                setDebugInfo(debugMsg);
                setError("로그인 정보 형식이 올바르지 않습니다.");
                setAuthStep(null);
                return;
            }

            if (!email) {
                console.error("handleNativeProfile: email을 추출할 수 없습니다.");
                setError("이메일 정보를 받지 못했습니다.");
                setAuthStep(null);
                return;
            }

            console.log("handleNativeProfile: 추출된 정보:", { email, name, hasIdToken: !!idToken });

            // 기타 정보 저장 (서버 이름/이메일이 오버라이드할 수 있음)
            if (idToken) {
                const payload = parseJwt(idToken);
                if (payload && payload.sub) {
                    localStorage.setItem("hb_googleSub", payload.sub);
                }
            }
            if (name) {
                localStorage.setItem("hb_googleName", name);
            }
            if (picture) {
                localStorage.setItem("hb_googlePicture", picture);
            }

            // 백엔드 호출에 사용할 토큰/프로필 저장
            lastTokenRef.current = { idToken: idToken || null, accessToken: null };
            lastProfileRef.current = {
                email,
                name,
                picture,
                phone: null, // 회원가입 시 직접 입력
            };

            // ========== 로그: RN에서 받은 데이터 확인 ==========
            console.log("========================================");
            console.log("📱 [RN 데이터 수신] handleNativeProfile 시작");
            console.log("========================================");
            console.log("✅ RN에서 받은 원본 데이터:", JSON.stringify(profile, null, 2));
            console.log("✅ 추출된 정보:", {
                email: email || "없음",
                name: name || "없음",
                picture: picture ? "있음" : "없음",
                hasIdToken: !!idToken,
                idTokenLength: idToken ? idToken.length : 0
            });
            console.log("========================================");

            // 1) 백엔드에 로그인 정보 전송 → email 기준으로 상태 확인
            console.log("🌐 [서버 통신 시작] 백엔드에 로그인 정보 전송");
            console.log("   - API URL:", `${API_BASE_URL}/auth/google`);
            console.log("   - 전송할 데이터:", {
                hasIdToken: !!idToken,
                email: email,
                profileName: name,
                hasProfilePicture: !!picture
            });

            let backendResult;
            let serverError = null;

            try {
                // idToken이 있으면 callAuthGoogle 사용
                if (idToken) {
                    console.log("🔑 [서버 통신] idToken이 있으므로 callAuthGoogle 호출");
                    backendResult = await callAuthGoogle({
                        idToken,
                        profilePicture: picture,
                    });
                    console.log("✅ [서버 통신 성공] callAuthGoogle 응답:", JSON.stringify(backendResult, null, 2));
                } else {
                    // email만 있는 경우, /auth/google에 email과 profilePicture만 전달
                    console.log("📧 [서버 통신] email만 있으므로 /auth/google 직접 호출");
                    try {
                        const requestBody = {
                            profilePicture: picture,
                            email: email,
                        };
                        console.log("   - 요청 Body:", JSON.stringify(requestBody, null, 2));

                        const res = await fetch(`${API_BASE_URL}/auth/google`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(requestBody),
                        });

                        console.log("   - 응답 Status:", res.status, res.statusText);

                        const data = await res.json();
                        console.log("   - 응답 Data:", JSON.stringify(data, null, 2));

                        if (!res.ok) {
                            console.error("❌ [서버 통신 실패] HTTP 오류:", res.status, data);
                            serverError = {
                                status: res.status,
                                message: data.message || "서버 확인 중 오류가 발생했습니다.",
                                data: data
                            };
                            // 404나 사용자가 없는 경우 need_signup 모드로 처리
                            if (res.status === 404 || data.mode === "need_signup") {
                                backendResult = {
                                    ok: false,
                                    mode: "need_signup",
                                    message: data.message || "회원가입이 필요합니다.",
                                };
                            } else {
                                backendResult = {
                                    ok: false,
                                    mode: "error",
                                    message: data.message || "서버 확인 중 오류가 발생했습니다.",
                                };
                            }
                        } else {
                            backendResult = data;
                            console.log("✅ [서버 통신 성공] /auth/google 응답:", JSON.stringify(backendResult, null, 2));
                        }
                    } catch (fetchError) {
                        console.error("❌ [서버 통신 실패] fetch 오류:", fetchError);
                        console.error("   - Error name:", fetchError?.name);
                        console.error("   - Error message:", fetchError?.message);
                        console.error("   - Error stack:", fetchError?.stack);
                        serverError = {
                            type: "fetch",
                            error: fetchError
                        };
                        // 네트워크 오류나 방화벽 문제로 서버 통신 실패
                        backendResult = {
                            ok: false,
                            mode: "network_error",
                            message: "서버에 연결할 수 없습니다.\n네트워크 또는 방화벽 설정을 확인해주세요.",
                        };
                    }
                }

                console.log("========================================");
                console.log("📊 [서버 통신 완료] 최종 결과:");
                console.log("   - ok:", backendResult?.ok);
                console.log("   - mode:", backendResult?.mode);
                console.log("   - status:", backendResult?.status || backendResult?.user?.status);
                console.log("   - message:", backendResult?.message);
                if (serverError) {
                    console.log("   - 서버 에러:", serverError);
                }
                console.log("========================================");
            } catch (apiError) {
                console.error("❌ [서버 통신 예외] API 호출 중 예외 발생:", apiError);
                console.error("   - Error name:", apiError?.name);
                console.error("   - Error message:", apiError?.message);
                console.error("   - Error stack:", apiError?.stack);
                serverError = {
                    type: "exception",
                    error: apiError
                };
                // 예외 발생 시에도 상태 페이지에 에러 메시지 표시
                backendResult = {
                    ok: false,
                    mode: "network_error",
                    message: "서버 연결 중 오류가 발생했습니다.\n네트워크를 확인해주세요.",
                };
            }

            if (!backendResult) {
                console.error("❌ [서버 통신 실패] 서버 응답이 null입니다.");
                backendResult = {
                    ok: false,
                    mode: "network_error",
                    message: "서버 응답을 받지 못했습니다.\n다시 시도해주세요.",
                };
            }

            // 사용자 정보 추출
            const serverUser = backendResult.user;
            // 백엔드 응답에서 status 확인 (pending 모드일 때는 user 객체가 없고 status만 있음)
            // 백엔드 상태가 wait으로 오더라도 바로 active로 간주하도록 보정
            const rawStatus = backendResult.status || serverUser?.status || null;
            const responseStatus = rawStatus === "wait" ? "active" : rawStatus;
            console.log("rawStatus", rawStatus)

            console.log("📊 [응답 분석] 백엔드 응답 분석:");
            console.log("   - ok:", backendResult.ok);
            console.log("   - mode:", backendResult.mode);
            console.log("   - status:", responseStatus);
            console.log("   - serverUser:", serverUser ? "있음" : "없음");

            // 1. need_signup 모드이거나 사용자가 없고 status도 없을 때 → 회원가입 안내 페이지
            if (backendResult.mode === "need_signup" || (!serverUser && !responseStatus && !backendResult.ok)) {
                console.log("📝 [사용자 없음] 회원가입 필요 - 회원가입 안내로 이동");
                console.log("   - mode:", backendResult.mode);
                console.log("   - serverUser:", serverUser);
                console.log("   - responseStatus:", responseStatus);
                console.log("   - backendResult.ok:", backendResult.ok);
                setSignupStep("confirm");
                setUserEmail(email || "");
                setUserName(name || "");

                // 회원가입 폼 초기화
                setSignupForm({
                    name: "",
                    phone: "",
                    birthdayYear: "",
                    birthdayMonth: "",
                    birthdayDay: "",
                    message: "",
                    level: "1",
                    position: "",
                    yearAssociation: "",
                    localAssociation: "",
                    church: "",
                });
                setError("");
                setDebugInfo("");
                return;
            }

            // 2. active 상태일 때 → 비밀번호 페이지로 이동
            if (backendResult.ok && serverUser && serverUser.status === "active") {
                console.log("✅ [사용자 상태] active - 비밀번호 페이지로 이동");
                setUserStatus("active");
                const finalEmail = serverUser?.email || email || "";
                const finalName = serverUser?.name || name || "";
                setUserName(finalName);
                setUserEmail(finalEmail);

                // 1초 동안 로딩 스피너 표시
                await showSpinnerFor("로그인 중입니다...");

                processLoginLogic(finalEmail, idToken, finalName, false);
                setError("");
                setDebugInfo("");
                return;
            }

            // 3. 사용자가 있거나 status가 있지만 active가 아닐 때 → 안내 페이지로 이동
            // (backendResult.mode === "pending"일 때는 user 객체가 없고 status만 있음)
            if ((serverUser && serverUser.status && serverUser.status !== "active") ||
                (backendResult.mode === "pending" && responseStatus && responseStatus !== "active") ||
                (responseStatus && responseStatus !== "active")) {
                const status = responseStatus;
                console.log("ℹ️ [사용자 상태] active 아님 - 안내 페이지로 이동");
                console.log("   - status:", status);
                console.log("   - mode:", backendResult.mode);
                console.log("   - serverUser 존재:", !!serverUser);

                setUserStatus(status);
                setUserEmailForActivate(email || "");

                // ready 상태일 때도 구글 프로필 정보를 localStorage에 저장
                if (status === "ready") {
                    console.log("💾 [localStorage 저장] ready 상태 - 구글 프로필 정보 저장");
                    if (email) {
                        localStorage.setItem("hb_googleEmail", email);
                        localStorage.setItem("hb_serverEmail", email);
                    }
                    if (name) {
                        localStorage.setItem("hb_googleName", name);
                        localStorage.setItem("hb_serverName", name);
                    }
                    if (picture) {
                        localStorage.setItem("hb_googlePicture", picture);
                    }
                }

                // status에 따라 안내 메시지 설정
                if (status === "wait") {
                    // wait를 active로 간주했으므로 여기로 오지 않지만, 혹시 모를 경우 대비
                    console.log("⏳ [사용자 상태] wait(보정) - active로 처리");
                    setStatusMessage("로그인 중입니다...");
                    setShowStatusMessage(false);
                } else if (status === "reject" || status === "hold") {
                    console.log("⏸️ [사용자 상태] reject/hold - 안내 페이지");
                    setStatusMessage("관리자에게 문의해주세요.\n(연락처: 1599-1873)");
                    setShowStatusMessage(true);
                } else if (status === "ready") {
                    console.log("✅ [사용자 상태] ready - 활성화 필요");
                    setStatusMessage("구글 인증에 성공했습니다.\n활성화 버튼을 눌러 myCert를 이용하세요");
                    setShowStatusMessage(true);
                } else if (backendResult.mode === "mismatch") {
                    console.log("⚠️ [사용자 상태] mismatch - 휴대폰 번호 불일치");
                    setStatusMessage("등록된 휴대폰 번호와 일치하지 않습니다.\n관리자에게 문의하세요.");
                    setShowStatusMessage(true);
                } else if (backendResult.mode === "network_error") {
                    console.log("🌐 [네트워크 오류] 서버 통신 실패");
                    setStatusMessage(backendResult.message || "서버에 연결할 수 없습니다.\n네트워크 또는 방화벽 설정을 확인해주세요.");
                    setShowStatusMessage(true);
                    setUserStatus(null);
                } else if (backendResult.mode === "error") {
                    console.log("❌ [서버 오류] 서버에서 오류 발생");
                    setStatusMessage(backendResult.message || "서버에서 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.");
                    setShowStatusMessage(true);
                    setUserStatus(null);
                } else {
                    // status가 있지만 위의 조건에 해당하지 않는 경우
                    console.log("❓ [사용자 상태] 알 수 없는 상태:", status, "mode:", backendResult.mode);
                    setStatusMessage(backendResult.message || "관리자 승인 대기중입니다.");
                    setShowStatusMessage(true);
                }
                setError("");
                setDebugInfo("");
                return;
            }
        } catch (e) {
            console.error("handleNativeProfile: 예외 발생:", e);
            const errorMessage = e?.message || e?.toString() || "알 수 없는 오류";
            console.error("handleNativeProfile: 에러 상세:", {
                message: errorMessage,
                stack: e?.stack,
                name: e?.name
            });

            // 에러 메시지 설정
            setError("로그인 처리 중 오류가 발생했습니다: " + errorMessage);

            // 회원정보가 없거나 에러가 발생한 경우 처리
            const isApp = isAppEnvironment();
            if (isApp) {
                // RN 환경에서는 로그인 화면으로 이동하지 않고 에러만 표시
                setAuthStep(null);
            } else {
                // 웹 환경에서는 로그인 화면 표시
                setAuthStep("google-signup");
            }

            // 에러 발생 시에도 기본 화면은 보여주기 위해 상태 초기화
            setDebugInfo("에러 상세:\n" + JSON.stringify({
                message: errorMessage,
                name: e?.name,
                stack: e?.stack?.split('\n').slice(0, 5).join('\n')
            }, null, 2));

            // 에러 상태 페이지 표시
            setStatusMessage("로그인 처리 중 오류가 발생했습니다.\n다시 시도해주세요.");
            setShowStatusMessage(true);
        }
    };

    // ----------------------------------------------------------------
    // 2-1. 마운트 시 서버/로컬에서 이름/이메일 로딩 (app/web 공통)
    //  - 우선 순위: 서버에서 가져온 값(hb_server*) → 기존 localStorage(hb_google*)
    // ----------------------------------------------------------------
    useEffect(() => {
        const serverEmail = localStorage.getItem("hb_serverEmail") || "";
        const serverName = localStorage.getItem("hb_serverName") || "";
        const savedEmail =
            serverEmail || localStorage.getItem("hb_googleEmail") || "";
        const savedName =
            serverName || localStorage.getItem("hb_googleName") || "";
        if (savedEmail) setUserEmail(savedEmail);
        if (savedName) setUserName(savedName);
    }, []);

    // ----------------------------------------------------------------
    // 2-2. 초기화 & 리스너 등록
    // ----------------------------------------------------------------
    // 페이지 진입 시 세션 체크 (한 번만 실행)

    useEffect(() => {
        console.log("========================================");
        console.log("🚀 [초기화] Login 컴포넌트 마운트");
        console.log("========================================");

        const handleNativeEvent = (event) => {
            console.log("========================================");
            console.log("📨 [이벤트 수신] NativeLoginSuccess 이벤트");
            console.log("========================================");
            console.log("   - Event detail:", JSON.stringify(event.detail, null, 2));
            handleNativeProfile(event.detail);
        };

        const handleNativeLogin = (profile) => {
            console.log("========================================");
            console.log("📞 [함수 호출] window.onNativeLogin");
            console.log("========================================");
            console.log("   - Profile:", JSON.stringify(profile, null, 2));
            handleNativeProfile(profile);
        };

        window.addEventListener("NativeLoginSuccess", handleNativeEvent);
        window.onNativeLogin = handleNativeLogin;
        console.log("✅ [초기화] 이벤트 리스너 등록 완료");

        const isApp = isAppEnvironment();
        console.log("🔍 [환경 감지]");
        console.log("   - isApp:", isApp);
        console.log("   - window.__MY_CERT_USER_DATA__:", window.__MY_CERT_USER_DATA__ ? "있음" : "없음");
        if (window.__MY_CERT_USER_DATA__) {
            console.log("   - __MY_CERT_USER_DATA__ 내용:", JSON.stringify(window.__MY_CERT_USER_DATA__, null, 2));
        }
        console.log("   - window.ReactNativeWebView:", !!window.ReactNativeWebView);
        console.log("   - window.isNativeApp:", !!window.isNativeApp);

        if (window.__MY_CERT_USER_DATA__) {
            // 앱에서 데이터 주입된 경우 - 바로 DB 확인 및 상태 페이지로 이동
            try {
                console.log("========================================");
                console.log("✅ [데이터 발견] window.__MY_CERT_USER_DATA__ 있음");
                console.log("========================================");
                console.log("   - 데이터 내용:", JSON.stringify(window.__MY_CERT_USER_DATA__, null, 2));
                console.log("   - handleNativeProfile 호출 시작");
                handleNativeProfile(window.__MY_CERT_USER_DATA__);
            } catch (err) {
                console.error("❌ [에러] 주입된 데이터 처리 중 에러:", err);
                console.error("   - Error name:", err?.name);
                console.error("   - Error message:", err?.message);
                console.error("   - Error stack:", err?.stack);
                setError("로그인 데이터 처리 중 오류가 발생했습니다: " + (err?.message || "알 수 없는 오류"));
                setAuthStep(null);
            }
        } else if (isApp) {
            // 앱 환경이지만 데이터가 아직 전달되지 않은 경우
            // 로그인 정보가 없으면 즉시 회원가입 안내로 이동
            const hasUserData = !!window.__MY_CERT_USER_DATA__;
            const hasEmail = !!(localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail"));

            if (!hasUserData && !hasEmail) {
                // 로그인 정보가 없으면 즉시 회원가입 안내로 이동
                console.log("📝 [회원가입 필요] 로그인 정보 없음 - 회원가입 안내로 이동");
                setSignupStep("confirm");
                const savedEmail = localStorage.getItem("hb_googleEmail") || "";
                const savedName = localStorage.getItem("hb_googleName") || "";
                if (savedEmail) setUserEmail(savedEmail);
                if (savedName) setUserName(savedName);

                // 회원가입 폼 초기화
                setSignupForm({
                    name: "",
                    phone: "",
                    birthdayYear: "",
                    birthdayMonth: "",
                    birthdayDay: "",
                    message: "",
                    level: "1",
                    position: "",
                    yearAssociation: "",
                    localAssociation: "",
                    church: "",
                });
                return; // 회원가입 화면으로 이동했으므로 여기서 종료
            }

            // 로그인 정보가 있으면 로딩 상태 유지
            console.log("⏳ [대기 중] 앱 환경이지만 로그인 데이터가 아직 전달되지 않음");
            console.log("   - 10초 후 타임아웃 설정");
            // authStep을 null로 설정하여 구글 로그인 화면이 보이지 않도록 함
            setAuthStep(null);
            // 타임아웃 설정 (10초 후 에러 표시)
            const timeout = setTimeout(() => {
                console.error("========================================");
                console.error("⏰ [타임아웃] 로그인 데이터를 10초 동안 받지 못함");
                console.error("========================================");
                setError("로그인 정보를 받지 못했습니다. 앱을 다시 시작해주세요.");
                setDebugInfo("앱 환경이지만 로그인 데이터가 10초 내에 전달되지 않았습니다.");
            }, 10000);

            return () => {
                clearTimeout(timeout);
                window.removeEventListener("NativeLoginSuccess", handleNativeEvent);
                window.onNativeLogin = null;
            };
        } else {
            // 웹 브라우저인 경우 - 구글 로그인 안내 화면 표시
            const hasGoogleAccount =
                localStorage.getItem("hb_hasGoogleAccount") === "true";

            if (hasGoogleAccount) {
                setAuthStep("google-login");
            } else {
                setAuthStep("google-signup");
            }
        }

        return () => {
            window.removeEventListener("NativeLoginSuccess", handleNativeEvent);
            window.onNativeLogin = null;
        };
    }, []);

    // ----------------------------------------------------------------
    // 3. 구글 스크립트 (앱 환경에서만 로드 금지, 웹은 dev/운영 동일하게 사용)
    // ----------------------------------------------------------------
    useEffect(() => {
        // 네이티브 앱 환경에서는 웹용 Google 스크립트 로드 불필요
        if (isAppEnvironment()) return;

        if (window.google && window.google.accounts) {
            setGoogleReady(true);
            return;
        }

        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google) setGoogleReady(true);
        };
        script.onerror = () => { };
        document.body.appendChild(script);
        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
        };
    }, [devMode]);

    // ----------------------------------------------------------------

    // ----------------------------------------------------------------
    // 4. 웹용 구글 로그인 핸들러
    //    - dev / 운영 모두 동일하게 Google OAuth + 동일 클라이언트 ID 사용
    // ----------------------------------------------------------------
    const handleGoogleAuth = async () => {
        // ★ (사용 안 함) 예전 DEV 전용 목업 로그인 분기 - 항상 비활성화
        // if (false && devMode) {
        //     console.log("[DEV] 구글 OAuth 스킵, 목업 데이터로 로그인 처리 + DB 저장");

        //     const fallbackEmail = "devuser@mycert.dev";
        //     const fallbackName = "개발 테스트 계정";

        //     const devEmail =
        //         localStorage.getItem("hb_serverEmail") ||
        //         localStorage.getItem("hb_googleEmail") ||
        //         fallbackEmail;
        //     const devName =
        //         localStorage.getItem("hb_serverName") ||
        //         localStorage.getItem("hb_googleName") ||
        //         fallbackName;

        //     // 구글 프로필 정보 저장 (회원가입 시 사용)
        //     lastTokenRef.current = {
        //         idToken: null,
        //         accessToken: "dev-access-token",
        //     };
        //     lastProfileRef.current = {
        //         email: devEmail,
        //         name: devName,
        //         picture: null,
        //         phone: null,
        //     };

        //     // 1) 백엔드에 목업 계정 정보 확인
        //     const backendResult = await syncDevLoginToBackend({
        //         email: devEmail,
        //         name: devName,
        //     });

        //     if (!backendResult) return;

        //     // active 상태일 때는 바로 메인으로 이동
        //     const serverUser = backendResult.user;
        //     if (backendResult.ok && serverUser && serverUser.status === "active") {
        //         const finalEmail = serverUser?.email || devEmail;
        //         const finalName = serverUser?.name || devName;

        //         setUserEmail(finalEmail);
        //         setUserName(finalName);

        //         // 1초 동안 로딩 스피너 표시
        //         await showSpinnerFor("로그인 중입니다...");

        //         // 2) OTP / 로그인 로직 수행
        //         processLoginLogic(finalEmail, "dev-access-token", finalName, false);
        //         return;
        //     }

        //     // active가 아닌 상태일 때는 안내 페이지로 이동
        //     if (!backendResult.ok) {
        //         const status = backendResult.status || null;
        //         setUserStatus(status);
        //         setUserEmailForActivate(devEmail);

        //         // ready 상태일 때도 구글 프로필 정보를 localStorage에 저장
        //         if (status === "ready") {
        //             if (devEmail) {
        //                 localStorage.setItem("hb_googleEmail", devEmail);
        //                 localStorage.setItem("hb_serverEmail", devEmail);
        //             }
        //             if (devName) {
        //                 localStorage.setItem("hb_googleName", devName);
        //                 localStorage.setItem("hb_serverName", devName);
        //             }
        //         }

        //         if (backendResult.mode === "mismatch") {
        //             setStatusMessage("등록된 휴대폰 번호와 일치하지 않습니다.\n관리자에게 문의하세요.");
        //             setShowStatusMessage(true);
        //         } else if (backendResult.mode === "pending") {
        //             // status에 따라 다른 메시지 표시
        //             if (status === "wait") {
        //                 setStatusMessage("관리자 승인 대기중입니다.\n관리자에게 문의하시려면 아래 번호로 문의해주세요.\n(연락처: 1599-1873)");
        //             } else if (status === "reject" || status === "hold") {
        //                 setStatusMessage("관리자에게 문의해주세요.\n(연락처: 1599-1873)");
        //             } else if (status === "ready") {
        //                 setStatusMessage("구글 인증에 성공했습니다.\n활성화 버튼을 눌러 myCert를 이용하세요");
        //             } else {
        //                 setStatusMessage("관리자 승인 대기중입니다.");
        //             }
        //             setShowStatusMessage(true);
        //         } else if (backendResult.mode === "need_signup") {
        //             // 회원가입 안내 단계로 전환
        //             setSignupStep("confirm");
        //             setUserEmail(devEmail);
        //             setUserName(devName);

        //             // dev 모드: 회원가입 폼 초기화 (이름은 빈칸, 구글 이름은 화면에 표시하지 않음)
        //             setSignupForm({
        //                 name: "", // 빈칸으로 초기화
        //                 phone: "",
        //                 message: "",
        //             });
        //         } else {
        //             setStatusMessage(backendResult.message || "로그인 처리 중 오류가 발생했습니다.");
        //             setShowStatusMessage(true);
        //         }
        //         return;
        //     }
        // }

        // dev / 운영 모두 동일한 클라이언트 ID 사용
        const clientId = process.env.REACT_APP_GOOGLE_WEB_API_ID;
        if (!clientId) {
            console.error('[Google OAuth] REACT_APP_GOOGLE_WEB_API_ID가 설정되지 않았습니다.');
            setError('Google OAuth 클라이언트 ID가 설정되지 않았습니다.');
            return;
        }
        if (!window.google) {
            console.error('[Google OAuth] window.google이 로드되지 않았습니다.');
            setError('Google OAuth 라이브러리가 로드되지 않았습니다.');
            return;
        }

        console.log('[Google OAuth] 클라이언트 ID:', clientId);
        console.log('[Google OAuth] 현재 도메인:', window.location.hostname);

        if (!googleClientRef.current) {
            googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: "openid email profile",
                callback: async (res) => {
                    if (res.error) {
                        console.error('[Google OAuth] 에러 발생:', res.error);
                        if (res.error === 'access_denied') {
                            setError('Google OAuth 접근이 거부되었습니다. Google Cloud Console에서 OAuth 동의 화면을 프로덕션으로 게시하거나 테스트 사용자로 등록해주세요.');
                        } else {
                            setError(`Google OAuth 오류: ${res.error}`);
                        }
                        return;
                    }
                    if (res.access_token) {
                        try {
                            // 1) accessToken으로 구글 기본 프로필 조회
                            const userInfoResponse = await fetch(
                                "https://www.googleapis.com/oauth2/v3/userinfo",
                                {
                                    headers: {
                                        Authorization: `Bearer ${res.access_token}`,
                                    },
                                }
                            );
                            const userInfo = await userInfoResponse.json();
                            const { sub, email, name, picture } = userInfo;

                            // 2) 기본 정보 로컬 저장 (서버 값이 오버라이드할 수 있음)
                            localStorage.setItem("hb_googleSub", sub || "");
                            localStorage.setItem("hb_googleName", name || "");
                            localStorage.setItem("hb_googlePicture", picture || "");

                            lastTokenRef.current = {
                                idToken: null,
                                accessToken: res.access_token,
                            };
                            lastProfileRef.current = {
                                email,
                                name,
                                picture,
                                phone: null, // 회원가입 시 직접 입력
                            };

                            // 3) 백엔드에 accessToken 전달 → 상태 확인 및 로그인 처리 분기
                            const backendResult = await callAuthGoogle({
                                accessToken: res.access_token,
                                profilePicture: picture,
                            });
                            if (!backendResult) return;

                            // active 상태일 때는 바로 메인으로 이동
                            const serverUser = backendResult.user;
                            if (backendResult.ok && serverUser && serverUser.status === "active") {
                                setUserStatus("active");
                                const finalEmail = serverUser?.email || email || "";
                                const finalName = serverUser?.name || name || "";
                                setUserName(finalName);
                                setUserEmail(finalEmail);

                                // 1초 동안 로딩 스피너 표시
                                await showSpinnerFor("로그인 중입니다...");

                                processLoginLogic(finalEmail, res.access_token, finalName, false);
                                return;
                            }

                            // active가 아닌 상태일 때는 안내 페이지로 이동
                            if (!backendResult.ok) {
                                const status = backendResult.status || null;
                                setUserStatus(status);
                                setUserEmailForActivate(email || "");

                                // ready 상태일 때도 구글 프로필 정보를 localStorage에 저장
                                if (status === "ready") {
                                    if (email) {
                                        localStorage.setItem("hb_googleEmail", email);
                                        localStorage.setItem("hb_serverEmail", email);
                                    }
                                    if (name) {
                                        localStorage.setItem("hb_googleName", name);
                                        localStorage.setItem("hb_serverName", name);
                                    }
                                    if (picture) {
                                        localStorage.setItem("hb_googlePicture", picture);
                                    }
                                }

                                if (backendResult.mode === "mismatch") {
                                    setStatusMessage("등록된 휴대폰 번호와 일치하지 않습니다.\\n관리자에게 문의하세요.");
                                    setShowStatusMessage(true);
                                } else if (backendResult.mode === "pending") {
                                    // status에 따라 다른 메시지 표시
                                    if (status === "wait") {
                                        setStatusMessage("관리자 승인 대기중입니다.\n문의사항은 아래 연락처로 연락주세요.\n(연락처: 1599-1873)");
                                    } else if (status === "reject" || status === "hold") {
                                        setStatusMessage("관리자에게 문의해주세요.\n(연락처: 1599-1873)");
                                    } else if (status === "ready") {
                                        setStatusMessage("구글 인증에 성공했습니다.\n활성화 버튼을 눌러 myCert를 이용하세요");
                                    } else {
                                        setStatusMessage("관리자 승인 대기중입니다.");
                                    }
                                    setShowStatusMessage(true);
                                } else if (backendResult.mode === "need_signup") {
                                    // 회원가입 안내 단계로 전환
                                    setSignupStep("confirm");
                                    setUserEmail(email || "");
                                    setUserName(name || "");

                                    // 운영 모드: 회원가입 폼 초기화 (이름은 빈칸, 구글 이름은 화면에 표시하지 않음)
                                    setSignupForm({
                                        name: "", // 빈칸으로 초기화
                                        phone: "",
                                        message: "",
                                    });
                                } else {
                                    setStatusMessage(backendResult.message || "로그인 처리 중 오류가 발생했습니다.");
                                    setShowStatusMessage(true);
                                }
                                return;
                            }
                        } catch (err) {
                            console.error("Web Login Error", err);
                            setError("프로필 정보/백엔드 로그인 처리 중 오류가 발생했습니다.");
                        }
                    }
                },
            });
        }
        googleClientRef.current.requestAccessToken();
    };


    const finishLogin = async (shouldReAuth = false, skipNavigation = false) => {
        // 활성화 후 비밀번호 입력 성공 시
        if (shouldReAuth) {
            // 활성화 단계에서 이미 토큰을 받았는지 확인
            const existingToken = localStorage.getItem("hb_backendToken");

            console.log("finishLogin 호출 - 토큰 확인:", {
                shouldReAuth,
                hasToken: !!existingToken,
                tokenLength: existingToken?.length,
                skipNavigation
            });

            if (existingToken) {
                // 이미 토큰이 있으면 callAuthGoogle을 호출하지 않고 바로 메인으로 이동
                console.log("활성화 후 이미 토큰이 있으므로 callAuthGoogle을 호출하지 않습니다.");

                // /auth/me로 최신 사용자 정보 가져오기
                try {
                    const meRes = await fetch(`${API_BASE_URL}/auth/me`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${existingToken}`,
                        },
                    });
                    if (meRes.ok) {
                        const meData = await meRes.json();
                        const user = meData.user || {};
                        // 최신 사용자 정보를 localStorage에 저장
                        if (user.name) {
                            localStorage.setItem("hb_serverName", user.name);
                            localStorage.setItem("hb_googleName", user.name);
                        }
                        if (user.email) {
                            localStorage.setItem("hb_serverEmail", user.email);
                            localStorage.setItem("hb_googleEmail", user.email);
                        }
                        if (user.picture) {
                            localStorage.setItem("hb_googlePicture", user.picture);
                        }
                        if (user.id) {
                            localStorage.setItem("hb_backendUserId", String(user.id));
                        }
                        console.log("활성화 후 최신 사용자 정보를 가져왔습니다:", user);
                    }
                } catch (e) {
                    console.error("Failed to load user info from /auth/me:", e);
                }

                localStorage.setItem("hb_isLoggedIn", "true");

                if (!skipNavigation) {
                    // 1초 동안 로딩 스피너 표시
                    await showSpinnerFor("로그인 중입니다...");
                    navigate("/main", { replace: true });
                }
                return;
            }

            // 토큰이 없으면 기존 로직대로 callAuthGoogle 호출
            console.log("활성화 후 구글 인증 재호출 시작");
            const { idToken, accessToken } = lastTokenRef.current || {};
            const profile = lastProfileRef.current || {};

            console.log("lastTokenRef:", { idToken: !!idToken, accessToken: !!accessToken });
            console.log("lastProfileRef:", profile);

            if (!idToken && !accessToken) {
                console.error("구글 인증 정보가 없습니다. lastTokenRef:", lastTokenRef.current);
                setError("구글 인증 정보가 없습니다. 다시 로그인해 주세요.");
                return;
            }

            console.log("callAuthGoogle 호출 시작");
            // 구글 인증 재호출
            const backendResult = await callAuthGoogle({
                idToken,
                accessToken,
                profilePicture: profile.picture,
            });

            console.log("callAuthGoogle 결과:", backendResult ? { ok: backendResult.ok, hasUser: !!backendResult.user, hasToken: !!backendResult.token } : "null");

            if (!backendResult) {
                console.error("callAuthGoogle이 null을 반환했습니다.");
                setError("로그인에 실패했습니다. 다시 시도해 주세요.");
                return;
            }

            // active 상태일 때만 메인으로 이동
            const serverUser = backendResult.user;
            if (backendResult.ok && serverUser && serverUser.status === "active") {
                // callAuthGoogle에서 토큰이 저장되었는지 확인
                let token = localStorage.getItem("hb_backendToken");

                // 토큰이 없으면 backendResult에서 직접 가져오기
                if (!token && backendResult.token) {
                    token = backendResult.token;
                    localStorage.setItem("hb_backendToken", token);
                    console.log("토큰을 backendResult에서 가져와서 저장했습니다.");
                } else if (!token) {
                    // 토큰이 여전히 없으면 에러
                    console.error("활성화 후 토큰이 없습니다. backendResult:", backendResult);
                    setError("로그인 토큰을 받지 못했습니다. 다시 시도해 주세요.");
                    return;
                } else {
                    console.log("토큰이 이미 localStorage에 저장되어 있습니다.");
                }

                // 사용자 정보를 localStorage에 저장 (backendResult에서 직접 가져오기)
                if (serverUser.name) {
                    localStorage.setItem("hb_serverName", serverUser.name);
                    localStorage.setItem("hb_googleName", serverUser.name);
                }
                if (serverUser.email) {
                    localStorage.setItem("hb_serverEmail", serverUser.email);
                    localStorage.setItem("hb_googleEmail", serverUser.email);
                }
                if (serverUser.picture) {
                    localStorage.setItem("hb_googlePicture", serverUser.picture);
                }
                if (serverUser.id) {
                    localStorage.setItem("hb_backendUserId", String(serverUser.id));
                }

                localStorage.setItem("hb_isLoggedIn", "true");

                // 활성화 후 메인 페이지로 이동하기 전에 /auth/me API 호출하여 사용자 정보 가져오기
                if (token) {
                    try {
                        const meRes = await fetch(`${API_BASE_URL}/auth/me`, {
                            method: "GET",
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        if (meRes.ok) {
                            const meData = await meRes.json();
                            const user = meData.user || {};
                            // 사용자 정보를 localStorage에 저장 (서버에서 최신 정보 가져오기)
                            if (user.name) {
                                localStorage.setItem("hb_serverName", user.name);
                                localStorage.setItem("hb_googleName", user.name);
                            }
                            if (user.email) {
                                localStorage.setItem("hb_serverEmail", user.email);
                                localStorage.setItem("hb_googleEmail", user.email);
                            }
                            if (user.picture) {
                                localStorage.setItem("hb_googlePicture", user.picture);
                            }
                        }
                    } catch (e) {
                        console.error("Failed to load user info from /auth/me:", e);
                    }
                } else {
                    console.error("Token is missing after activation. backendResult:", backendResult);
                }

                // skipNavigation이 true이면 메인으로 이동하지 않음 (활성화 단계에서 비밀번호 입력으로 이동하기 위해)
                if (!skipNavigation) {
                    // 1초 동안 로딩 스피너 표시
                    await showSpinnerFor("로그인 중입니다...");
                    navigate("/main", { replace: true });
                } else {
                    console.log("활성화 단계: 토큰을 받았지만 메인으로 이동하지 않습니다. 비밀번호 입력 단계로 이동합니다.");
                }
            } else {
                setError("로그인에 실패했습니다. 상태를 확인해 주세요.");
            }
        } else {
            // 일반적인 비밀번호 입력 성공 시
            // 토큰이 있는지 확인하고, 없으면 다시 가져오기
            let token = localStorage.getItem("hb_backendToken");

            // 토큰이 없으면 (세션은 유효하지만 토큰이 제거된 경우) 다시 가져오기
            if (!token) {
                console.log("비밀번호 입력 성공했지만 토큰이 없음 - 토큰 재발급 시도");

                // 이메일로 사용자 정보 조회하여 토큰 재발급
                const email = localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail");
                if (email) {
                    try {
                        const { idToken, accessToken } = lastTokenRef.current || {};
                        const profile = lastProfileRef.current || {};

                        if (idToken || accessToken) {
                            const backendResult = await callAuthGoogle({
                                idToken,
                                accessToken,
                                profilePicture: profile.picture,
                            });

                            if (backendResult && backendResult.ok && backendResult.token) {
                                token = backendResult.token;
                                localStorage.setItem("hb_backendToken", token);
                                localStorage.setItem("hb_backendUserId", String(backendResult.user?.id || ""));
                                console.log("토큰 재발급 성공");
                            } else {
                                console.error("토큰 재발급 실패:", backendResult);
                                setError("로그인에 실패했습니다. 다시 시도해 주세요.");
                                return;
                            }
                        } else {
                            console.error("구글 인증 정보가 없어서 토큰을 재발급할 수 없습니다.");
                            setError("로그인 정보가 없습니다. 다시 로그인해 주세요.");
                            return;
                        }
                    } catch (e) {
                        console.error("토큰 재발급 중 오류:", e);
                        setError("로그인 중 오류가 발생했습니다.");
                        return;
                    }
                } else {
                    console.error("이메일 정보가 없어서 토큰을 재발급할 수 없습니다.");
                    setError("로그인 정보가 없습니다. 다시 로그인해 주세요.");
                    return;
                }
            }

            // 토큰이 있으면 정상적으로 진행
            localStorage.setItem("hb_isLoggedIn", "true");

            // 1초 동안 로딩 스피너 표시
            await showSpinnerFor("로그인 중입니다...");
            navigate("/main", { replace: true });
        }
    };


    const isApp = isAppEnvironment();
    // 앱 환경에서는 구글 로그인 화면을 보여주지 않음 (로그인 정보는 RN에서 전달됨)
    const isGoogleStep = !isApp && authStep && authStep.startsWith("google");

    // RN 환경에서 닫기 버튼 클릭 시 RN 로그인 화면으로 이동
    const handleCloseToNativeLogin = () => {
        if (isApp && window.ReactNativeWebView) {
            try {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({ type: "LOGOUT" })
                );
                console.log("📱 RN 로그인 화면으로 이동 요청");
                return true; // RN으로 이동했음을 반환
            } catch (e) {
                console.error("RN postMessage 오류:", e);
            }
        }
        return false; // RN 환경이 아니거나 전송 실패
    };


    // 회원가입 성공 화면이 표시되면 전체 화면으로 표시
    if (showSignupSuccess) {
        return (
            <div className="container-wrap login-wrap">
                {/* 상단 로고 영역 (빈 공간) */}
                <div style={{ height: "70px" }}></div>

                {/* 메인 콘텐츠 영역 */}
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    // padding: "40px 20px",
                    overflowY: "auto"
                }}>
                    {/* 체크 아이콘 */}
                    <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "#ff6a63",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "10px"
                    }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    {/* 메인 메시지 */}
                    <div style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#111",
                        marginBottom: "5px",
                        textAlign: "center"
                    }}>
                        본인확인을 완료했습니다.
                    </div>

                    {/* 서브 메시지 */}
                    <div style={{
                        fontSize: "16px",
                        color: "#111",
                        textAlign: "center",
                        lineHeight: "1.5"
                    }}>
                        로그인 후 서비스를 이용해주세요.
                    </div>
                    <div className="google-bottom-area" style={{ paddingBottom: "0" }}>
                        <button
                            className="google-signin-btn"
                            onClick={() => {
                                // RN 환경이면 RN 로그인 화면으로 이동
                                if (handleCloseToNativeLogin()) {
                                    return;
                                }
                                setShowSignupSuccess(false);
                                setSignupStep(null);
                                setError("");
                                setSignupForm({
                                    name: "",
                                    phone: "",
                                    birthdayYear: "",
                                    birthdayMonth: "",
                                    birthdayDay: "",
                                    message: "",
                                    level: "1",
                                    position: "",
                                    yearAssociation: "",
                                    localAssociation: "",
                                    church: ""
                                });
                            }}
                        >
                            <span className="google-signin-text">확인</span>
                        </button>
                    </div>
                </div>

                {/* 하단 버튼 영역 (고정) */}
            </div>
        );
    }

    // 상태별 안내 페이지 표시
    if (showStatusMessage) {
        return (
            <div className="container-wrap login-wrap">
                <div className="login-top-logo">
                    <img src="/img/myCert.png" alt="myCert" />
                </div>

                <div className="google-main google-main-large">
                    <img
                        src="/img/google_logo.png"
                        className="google-circle-icon"
                        style={{ marginBottom: 10 }}
                        alt="Google"
                    />
                    <div className="google-center-text">
                        <span>{statusMessage}</span>
                    </div>

                    {/* 버튼 영역 */}
                    <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", width: "100%", padding: "0 24px" }}>
                        {userStatus === "ready" && (
                            <>
                                <button
                                    className="google-signin-btn"
                                    onClick={handleActivate}
                                >
                                    <span className="google-signin-text">활성화하기</span>
                                </button>
                                <span
                                    className="login-close-text"
                                    onClick={() => {
                                        // RN 환경이면 RN 로그인 화면으로 이동
                                        if (handleCloseToNativeLogin()) {
                                            return;
                                        }
                                        setSignupStep(null);
                                        setError("");
                                        setSignupForm({
                                            name: "",
                                            phone: "",
                                            birthdayYear: "",
                                            birthdayMonth: "",
                                            birthdayDay: "",
                                            message: "",
                                            level: "1",
                                            position: "",
                                            yearAssociation: "",
                                            localAssociation: "",
                                            church: ""
                                        });
                                        setUserStatus(null);
                                    }}
                                    style={{ cursor: "pointer", display: "block", textAlign: "center" }}
                                >
                                    닫기
                                </span>
                            </>
                        )}

                        {/* 닫기 버튼 */}

                        {userStatus != "ready" && (
                            <button
                                className="google-signin-btn"
                                onClick={() => {
                                    // RN 환경이면 RN 로그인 화면으로 이동
                                    if (handleCloseToNativeLogin()) {
                                        return;
                                    }
                                    setShowStatusMessage(false);
                                    setStatusMessage("");
                                    setUserStatus(null);
                                    setError("");
                                }}
                            >
                                <span className="google-signin-text">닫기</span>
                            </button>
                        )}
                    </div>
                </div>




                {/* {(userStatus === "wait" || userStatus === "reject" || userStatus === "hold") && (
                    <div className="google-bottom-area">
                        <button className="google-bottom-btn" onClick={() => setShowAdminContactModal(true)}>
                            문의하기
                        </button>
                    </div>
                )} */}

                {/* 관리자 문의하기 모달 */}
                {showAdminContactModal && (
                    <div
                        className="setting-modal-overlay"
                        onClick={() => setShowAdminContactModal(false)}
                    >
                        <div
                            className="setting-modal"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "400px", width: "90%" }}
                        >
                            <div className="setting-modal-bar">
                                <b>관리자 문의</b>
                            </div>

                            <div className="setting-modal-body" style={{ padding: "24px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        color: "#333",
                                        padding: "12px 16px",
                                        background: "#f9f9f9",
                                        borderRadius: "8px",
                                        gap: "12px"
                                    }}
                                >
                                    <span
                                        style={{
                                            flex: 1,
                                            userSelect: "all",
                                            cursor: "text"
                                        }}
                                        onClick={(e) => {
                                            const range = document.createRange();
                                            range.selectNodeContents(e.target);
                                            const selection = window.getSelection();
                                            selection.removeAllRanges();
                                            selection.addRange(range);
                                        }}
                                    >
                                        {adminPhoneNumber}
                                    </span>
                                    <button
                                        style={{
                                            padding: "8px",
                                            background: "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            color: "#666",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0
                                        }}
                                        onClick={() => {
                                            // 하이픈 제거하고 숫자만 복사
                                            const phoneNumberOnly = adminPhoneNumber.replace(/-/g, '');
                                            if (navigator.clipboard && navigator.clipboard.writeText) {
                                                navigator.clipboard.writeText(phoneNumberOnly).then(() => {
                                                    alert('전화번호가 복사되었습니다.');
                                                }).catch(() => {
                                                    // 폴백: 텍스트 선택
                                                    const textArea = document.createElement('textarea');
                                                    textArea.value = phoneNumberOnly;
                                                    document.body.appendChild(textArea);
                                                    textArea.select();
                                                    document.execCommand('copy');
                                                    document.body.removeChild(textArea);
                                                    alert('전화번호가 복사되었습니다.');
                                                });
                                            } else {
                                                // 구형 브라우저 폴백
                                                const textArea = document.createElement('textarea');
                                                textArea.value = phoneNumberOnly;
                                                document.body.appendChild(textArea);
                                                textArea.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(textArea);
                                                alert('전화번호가 복사되었습니다.');
                                            }
                                        }}
                                        title="복사하기"
                                    >
                                        <FontAwesomeIcon icon={faCopy} style={{ fontSize: "18px" }} />
                                    </button>
                                </div>
                            </div>

                            <div className="setting-modal-actions" style={{ display: "flex", gap: "8px", flexDirection: (isMobileDevice() || isAppEnvironment()) ? "row" : "column" }}>
                                {(isMobileDevice() || isAppEnvironment()) && (
                                    <a
                                        href={`tel:${adminPhoneNumber.replace(/-/g, '')}`}
                                        className="setting-btn"
                                        style={{ flex: 1, textDecoration: "none", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}
                                    >
                                        전화하기
                                    </a>
                                )}
                                <button
                                    className="setting-btn outline"
                                    style={{ flex: 1 }}
                                    onClick={() => setShowAdminContactModal(false)}
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // 앱 환경에서 로그인 정보를 기다리는 중일 때 로딩 화면 표시
    // showStatusMessage가 true면 상태 페이지가 우선 표시되어야 함
    // signupStep이 있으면 회원가입 화면이 우선 표시되어야 함
    if (isApp && !authStep && !showStatusMessage && !signupStep) {
        // 로그인 정보가 없거나 회원가입이 필요한 경우 확인
        const hasUserData = !!window.__MY_CERT_USER_DATA__;
        const hasEmail = !!(localStorage.getItem("hb_serverEmail") || localStorage.getItem("hb_googleEmail"));

        // 로그인 정보가 없으면 회원가입 화면으로 이동 (위의 useEffect에서 처리됨)
        // 다음 렌더링에서 회원가입 화면이 표시되도록 null 반환
        if (!hasUserData && !hasEmail) {
            return null;
        }

        // 로그인 정보가 있으면 로딩 화면 표시
        return (
            <div className="container-wrap login-wrap">
                <div className="login-top-logo">
                    <img src="/img/myCert.png" alt="myCert" />
                </div>
                <div className="google-main google-main-large">
                    <img
                        src="/img/google_logo.png"
                        className="google-circle-icon"
                        style={{ marginBottom: 10 }}
                        alt="Google"
                    />
                    <div className="google-center-text">
                        <span>로그인 정보를 확인 중입니다...</span>
                    </div>
                </div>
                {error && (
                    <div className="login-error-message" style={{ marginTop: 20, textAlign: "center" }}>
                        {error}
                    </div>
                )}
                {debugInfo && (
                    <div
                        style={{
                            marginTop: 20,
                            padding: 10,
                            backgroundColor: "#f0f0f0",
                            fontSize: 10,
                            textAlign: "left",
                            borderRadius: 4,
                            maxHeight: 150,
                            overflowY: "auto",
                        }}
                    >
                        {debugInfo}
                    </div>
                )}
            </div>
        );
    }

    // 본인인증 페이지 (verify 단계)
    if (signupStep === "verify") {
        return (
            <div className="container-wrap login-wrap">
                {/* 헤더 (50px) - 상단 고정 */}
                <div
                    style={{
                        width: "100%",
                        height: "60px",
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 20px",
                        background: "#fff",
                    }}
                >
                    {/* 뒤로가기 버튼 */}
                    <button
                        onClick={() => {
                            setSignupStep("confirm");
                        }}
                        style={{
                            background: "transparent",
                            border: "none",
                            fontSize: "20px",
                            cursor: "pointer",
                            color: "#333",
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                </div>

                <div
                    className="google-main google-main-large"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "100dvh",
                        width: "100%",
                        padding: 0,
                        paddingTop: "60px", // 헤더 높이만큼 패딩 추가
                        background: "#fff",
                        position: "relative",
                        justifyContent: "flex-start"
                    }}
                >

                    <div style={{ width: "95%", flexShrink: 0 }}>
                        <img
                            src="/img/example_img.png"
                            alt="본인인증 예시"
                            style={{ width: "100%", display: "block", objectFit: "contain" }}
                        />
                    </div>

                    <div
                        style={{
                            position: "fixed",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            width: "100%",
                            padding: 0,
                            background: "#323c59",
                            zIndex: 1000
                        }}
                    >
                        <button
                            style={{
                                width: "100%",
                                padding: "30px 20px 50px",
                                border: "none",
                                borderRadius: 0,
                                fontSize: "17px",
                                fontWeight: 800,
                                background: "transparent",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "12px",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                setVerifyLoading(true);
                                setTimeout(() => {
                                    setVerifyLoading(false);
                                    setSignupStep("pass");
                                }, 600);
                            }}
                        >
                            <span>본인확인 하기</span>
                            <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "14px" }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 본인인증 완료 전 확인 화면 (pass 단계)
    if (signupStep === "pass") {
        return (
            <div
                className="container-wrap login-wrap"
                style={{ background: "#fff", minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center" }}
            >
                <div style={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                    {verifyLoading && (
                        <div style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
                            <div className="spinner" />
                        </div>
                    )}
                    <img
                        src="/img/pass_img.png"
                        alt="본인확인"
                        style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            margin: "0 auto",
                        }}
                        onClick={async () => {
                            setVerifyLoading(true);
                            
                            // 회원가입 폼에 기본값 설정
                            setSignupForm((f) => ({
                                ...f,
                                name: "성춘향",
                                phone: "010-1234-1234",
                                email: userEmail || "",
                            }));
                            
                            // 회원가입 제출
                            try {
                                const { idToken, accessToken } = lastTokenRef.current || {};
                                const profile = lastProfileRef.current || {};
                                
                                if (!idToken && !accessToken) {
                                    setError("구글 인증 정보가 없습니다. 다시 로그인해 주세요.");
                                    setVerifyLoading(false);
                                    return;
                                }
                                
                                const phoneDigitsOnly = "01012341234"; // "010-1234-1234"에서 하이픈 제거
                                const isDevMode = devMode || (accessToken && accessToken.startsWith('dev-'));
                                
                                const requestBody = {
                                    name: "성춘향",
                                    phone: phoneDigitsOnly,
                                    birthday: null,
                                    message: "",
                                    profilePicture: profile.picture || null,
                                    profileName: profile.name || null,
                                    level: "1",
                                    position: null,
                                    yearAssociation: null,
                                    localAssociation: null,
                                    church: null,
                                    status: "active", // 요청에 따라 가입 시 바로 active 처리
                                };
                                
                                if (isDevMode) {
                                    requestBody.accessToken = accessToken || "dev-access-token";
                                    requestBody.email = userEmail || profile.email;
                                } else {
                                    if (idToken) requestBody.idToken = idToken;
                                    if (accessToken) requestBody.accessToken = accessToken;
                                }
                                
                                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
                                const res = await fetch(`${API_BASE_URL}/auth/register`, {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify(requestBody),
                                });
                                
                                const data = await res.json();
                                if (!res.ok || !data.ok) {
                                    const errorMsg = data.message || data.error || "회원가입 중 오류가 발생했습니다.";
                                    setError(errorMsg);
                                    setVerifyLoading(false);
                                    return;
                                }
                                
                                // 성공 화면으로 이동
                                setVerifyLoading(false);
                                setShowSignupSuccess(true);
                                setError("");
                            } catch (e) {
                                console.error("register error", e);
                                setError("회원가입 중 오류가 발생했습니다.");
                                setVerifyLoading(false);
                            }
                        }}
                    />
                </div>
            </div>
        );
    }


    return (
        <div className="container-wrap login-wrap">
            <div className="login-top-logo">
                <img src="/img/myCert.png" alt="myCert" />
            </div>

            {/* {isGoogleStep ? ( */}
            {isGoogleStep || (!isGoogleStep && !showStatusMessage && (signupStep === "confirm" || signupStep === "verify")) ? (
                <>
                    {signupStep === "phone" ? (
                        <>
                            {/* 기본정보 입력 단계는 더 이상 사용하지 않음 */}
                        </>
                    ) : signupStep === "message" ? (
                        <>
                            {/* 가입 메시지 입력 단계는 더 이상 사용하지 않음 */}
                        </>
                    ) : signupStep === "phone" ? (
                        <>
                            {/* 기본정보 입력 단계 (휴대폰번호 + 가입사유) */}
                            <div className="google-regist google-main-large" style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100vh",
                                width: "100%",
                                maxWidth: "100%",
                                padding: "0"
                            }}>
                                {/* 고정된 타이틀 영역 */}
                                <div style={{
                                    padding: "20px 20px 5px",
                                    background: "#fff",
                                    // borderBottom: "1px solid #e5e7eb",
                                    flexShrink: 0
                                }}>
                                    <div style={{
                                        fontSize: "22px",
                                        color: "#111"
                                    }}>
                                        <span style={{ fontWeight: "bold" }}>기본정보</span>를 입력해주세요.
                                    </div>
                                </div>

                                {/* 스크롤 가능한 입력 필드 영역 */}
                                <div style={{
                                    flex: 1,
                                    overflowY: "auto",
                                    padding: "0 20px 70px",
                                    width: "100%"
                                }}>
                                    {/* 이메일 (읽기 전용) */}
                                    <div style={{ marginBottom: "10px", width: "100%" }}>
                                        <input
                                            type="email"
                                            value={userEmail}
                                            readOnly
                                            disabled
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#f5f5f5",
                                                color: "#666",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 이름 (수정 가능) */}
                                    <div style={{ marginBottom: "10px", width: "100%" }}>
                                        <input
                                            type="text"
                                            value={signupForm.name || ""}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    name: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            placeholder="이름을 입력하세요"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                color: "#111",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 휴대폰 번호 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <input
                                            type="tel"
                                            value={signupForm.phone}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    phone: formatPhoneKOR(e.target.value),
                                                }));
                                                // 입력 시작 시 에러 메시지 제거
                                                if (error) {
                                                    setError("");
                                                }
                                            }}
                                            placeholder="010-1234-5678"
                                            inputMode="numeric"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 생년월일 입력 (년, 월, 일 select 박스) */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                                            {/* 년도 선택 */}
                                            <select
                                                value={signupForm.birthdayYear}
                                                onChange={(e) => {
                                                    setSignupForm((f) => ({
                                                        ...f,
                                                        birthdayYear: e.target.value,
                                                        birthdayDay: "", // 년도 변경 시 일 초기화
                                                    }));
                                                    if (error) setError("");
                                                }}
                                                style={{
                                                    flex: 1,
                                                    height: "50px",
                                                    padding: "0 16px",
                                                    fontSize: "16px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "12px",
                                                    background: "#fff",
                                                    outline: "none",
                                                    boxSizing: "border-box",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                <option value="">년도</option>
                                                {(() => {
                                                    // 2025년부터 과거 방향으로 (예: 2025, 2024, ... 1900)
                                                    const startYear = 2025;
                                                    const endYear = 1900;
                                                    const count = startYear - endYear + 1;
                                                    return Array.from({ length: count }, (_, idx) => {
                                                        const year = startYear - idx;
                                                        return (
                                                            <option key={year} value={year}>
                                                                {year}년
                                                            </option>
                                                        );
                                                    });
                                                })()}
                                            </select>

                                            {/* 월 선택 */}
                                            <select
                                                value={signupForm.birthdayMonth}
                                                onChange={(e) => {
                                                    setSignupForm((f) => ({
                                                        ...f,
                                                        birthdayMonth: e.target.value,
                                                        birthdayDay: "", // 월 변경 시 일 초기화
                                                    }));
                                                    if (error) setError("");
                                                }}
                                                style={{
                                                    flex: 1,
                                                    height: "50px",
                                                    padding: "0 16px",
                                                    fontSize: "16px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "12px",
                                                    background: "#fff",
                                                    outline: "none",
                                                    boxSizing: "border-box",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                <option value="">월</option>
                                                {Array.from({ length: 12 }, (_, i) => {
                                                    const month = i + 1;
                                                    return (
                                                        <option key={month} value={month.toString().padStart(2, "0")}>
                                                            {month}월
                                                        </option>
                                                    );
                                                })}
                                            </select>

                                            {/* 일 선택 */}
                                            <select
                                                value={signupForm.birthdayDay}
                                                onChange={(e) => {
                                                    setSignupForm((f) => ({
                                                        ...f,
                                                        birthdayDay: e.target.value,
                                                    }));
                                                    if (error) setError("");
                                                }}
                                                style={{
                                                    flex: 1,
                                                    height: "50px",
                                                    padding: "0 16px",
                                                    fontSize: "16px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "12px",
                                                    background: "#fff",
                                                    outline: "none",
                                                    boxSizing: "border-box",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                <option value="">일</option>
                                                {(() => {
                                                    // 선택된 년도와 월에 따라 일수 계산
                                                    if (!signupForm.birthdayYear || !signupForm.birthdayMonth) {
                                                        return null; // 년도나 월이 선택되지 않으면 일 선택 비활성화
                                                    }
                                                    const year = parseInt(signupForm.birthdayYear);
                                                    const month = parseInt(signupForm.birthdayMonth);
                                                    const daysInMonth = new Date(year, month, 0).getDate(); // 해당 월의 일수 계산
                                                    return Array.from({ length: daysInMonth }, (_, i) => {
                                                        const day = i + 1;
                                                        return (
                                                            <option key={day} value={day.toString().padStart(2, "0")}>
                                                                {day}일
                                                            </option>
                                                        );
                                                    });
                                                })()}
                                            </select>
                                        </div>
                                    </div>

                                    {/* 등급 선택 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <select
                                            value={signupForm.level}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    level: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <option value="1">정회원</option>
                                            <option value="2">준회원</option>
                                        </select>
                                    </div>

                                    {/* 직책 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <input
                                            type="text"
                                            value={signupForm.position}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    position: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            placeholder="호칭"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 연회 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <input
                                            type="text"
                                            value={signupForm.yearAssociation}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    yearAssociation: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            placeholder="연회"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 지방회 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <input
                                            type="text"
                                            value={signupForm.localAssociation}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    localAssociation: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            placeholder="지방회"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 교회 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <input
                                            type="text"
                                            value={signupForm.church}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    church: e.target.value,
                                                }));
                                                if (error) setError("");
                                            }}
                                            placeholder="교회"
                                            style={{
                                                width: "100%",
                                                height: "50px",
                                                padding: "0 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                    </div>

                                    {/* 메모 입력 */}
                                    <div style={{ marginBottom: "12px", width: "100%" }}>
                                        <textarea
                                            value={signupForm.message}
                                            onChange={(e) => {
                                                setSignupForm((f) => ({
                                                    ...f,
                                                    message: e.target.value,
                                                }));
                                                // 입력 시작 시 에러 메시지 제거
                                                if (error) {
                                                    setError("");
                                                }
                                            }}
                                            placeholder="메모를 입력해주세요"
                                            rows={4}
                                            style={{
                                                width: "100%",
                                                padding: "12px 16px",
                                                fontSize: "16px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "12px",
                                                background: "#fff",
                                                outline: "none",
                                                resize: "vertical",
                                                boxSizing: "border-box",
                                                fontFamily: "inherit"
                                            }}
                                        />
                                    </div>

                                    {error && (
                                        <div className="login-error-message" style={{ marginBottom: "12px" }}>
                                            {error}
                                        </div>
                                    )}
                                </div>

                                {/* 하단 고정 버튼 영역 */}
                                <div style={{
                                    padding: "5px 20px",
                                    background: "#fff",
                                    // borderTop: "1px solid #e5e7eb",
                                    position: "sticky",
                                    bottom: 0,
                                    zIndex: 10
                                }}>
                                    {/* 기본정보 입력 버튼 */}
                                    {(() => {
                                        // 필수 필드 검증: 메모를 제외한 모든 필드가 입력되어야 함
                                        const phoneDigits = signupForm.phone ? signupForm.phone.replace(/\D/g, "") : "";
                                        const hasValidPhone = phoneDigits.length >= 10;
                                        const hasValidName = signupForm.name && signupForm.name.trim() !== "";
                                        const hasValidBirthday = signupForm.birthdayYear && signupForm.birthdayMonth && signupForm.birthdayDay;
                                        const hasValidPosition = signupForm.position && signupForm.position.trim() !== "";
                                        const hasValidYearAssociation = signupForm.yearAssociation && signupForm.yearAssociation.trim() !== "";
                                        const hasValidLocalAssociation = signupForm.localAssociation && signupForm.localAssociation.trim() !== "";
                                        const hasValidChurch = signupForm.church && signupForm.church.trim() !== "";

                                        // 메모를 제외한 모든 필드가 입력되어야 함
                                        const isFormValid = hasValidName && hasValidPhone && hasValidBirthday &&
                                            hasValidPosition && hasValidYearAssociation &&
                                            hasValidLocalAssociation && hasValidChurch;

                                        return (
                                            <button
                                                disabled={!isFormValid}
                                                style={{
                                                    width: "100%",
                                                    padding: "14px 0",
                                                    borderRadius: "12px",
                                                    border: "none",
                                                    background: isFormValid ? "#ff6a63" : "#d1d5db",
                                                    color: "#fff",
                                                    fontSize: "16px",
                                                    fontWeight: "600",
                                                    cursor: isFormValid ? "pointer" : "not-allowed",
                                                    // marginBottom: "10px",
                                                    opacity: isFormValid ? 1 : 0.6,
                                                    transition: "background-color 0.2s ease"
                                                }}
                                                onClick={isFormValid ? handleSignupSubmit : undefined}
                                            >
                                                기본정보 입력
                                            </button>
                                        );
                                    })()}

                                    {/* 닫기 텍스트 */}
                                    <span
                                        className="login-close-text"
                                        onClick={() => {
                                            // RN 환경이면 RN 로그인 화면으로 이동
                                            if (handleCloseToNativeLogin()) {
                                                return;
                                            }
                                            setSignupStep(null);
                                            setError("");
                                            setSignupForm({
                                                name: "",
                                                phone: "",
                                                birthdayYear: "",
                                                birthdayMonth: "",
                                                birthdayDay: "",
                                                message: "",
                                                level: "1",
                                                position: "",
                                                yearAssociation: "",
                                                localAssociation: "",
                                                church: ""
                                            });
                                            setUserStatus(null);
                                        }}
                                        style={{ cursor: "pointer", display: "block", textAlign: "center" }}
                                    >
                                        닫기
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : signupStep === "message" ? (
                        <>
                            {/* 가입 메시지 입력 단계 (더 이상 사용하지 않음 - phone 단계에서 통합) */}
                            <div className="google-regist google-main-large" style={{ padding: "40px 20px 20px", width: "100%", maxWidth: "100%" }}>

                                <div style={{
                                    fontSize: "22px",
                                    color: "#111",
                                    marginBottom: "20px"
                                }}>
                                    <span style={{ fontWeight: "bold" }}>가입메세지</span>를
                                    <br />
                                    입력해주세요
                                </div>

                                {/* 이메일 (읽기 전용) */}
                                <div style={{ marginBottom: "10px", width: "100%" }}>
                                    <input
                                        type="email"
                                        value={userEmail}
                                        readOnly
                                        disabled
                                        style={{
                                            width: "100%",
                                            height: "50px",
                                            padding: "0 16px",
                                            fontSize: "16px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "12px",
                                            background: "#f5f5f5",
                                            color: "#666",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                </div>

                                {/* 이름 (읽기 전용) */}
                                <div style={{ marginBottom: "10px", width: "100%" }}>
                                    <input
                                        type="text"
                                        value={signupForm.name !== null ? signupForm.name : (userName || "")}
                                        readOnly
                                        disabled
                                        style={{
                                            width: "100%",
                                            height: "50px",
                                            padding: "0 16px",
                                            fontSize: "16px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "12px",
                                            background: "#f5f5f5",
                                            color: "#666",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                </div>

                                {/* 휴대폰 번호 (읽기 전용) */}
                                <div style={{ marginBottom: "10px", width: "100%" }}>
                                    <input
                                        type="tel"
                                        value={signupForm.phone}
                                        readOnly
                                        disabled
                                        style={{
                                            width: "100%",
                                            height: "50px",
                                            padding: "0 16px",
                                            fontSize: "16px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "12px",
                                            background: "#f5f5f5",
                                            color: "#666",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                </div>

                                {/* 가입 메시지 입력 */}
                                <div style={{ position: "relative", marginBottom: "10px", width: "100%" }}>
                                    <textarea
                                        value={signupForm.message}
                                        maxLength={50}
                                        onChange={(e) =>
                                            setSignupForm((f) => ({
                                                ...f,
                                                message: e.target.value,
                                            }))
                                        }
                                        placeholder="가입사유"
                                        autoFocus
                                        style={{
                                            width: "100%",
                                            height: "120px",
                                            padding: "16px",
                                            fontSize: "16px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "12px",
                                            background: "#fff",
                                            outline: "none",
                                            resize: "none",
                                            fontFamily: "inherit",
                                            textAlign: "left",
                                            whiteSpace: "pre-wrap",
                                            wordWrap: "break-word",
                                            boxSizing: "border-box"
                                        }}
                                    />
                                    <div style={{
                                        position: "absolute",
                                        bottom: "12px",
                                        right: "16px",
                                        fontSize: "12px",
                                        color: "#999",
                                        pointerEvents: "none"
                                    }}>
                                        {signupForm.message.length}/50
                                    </div>
                                </div>

                                {error && (
                                    <div className="login-error-message-signup">
                                        {error}
                                    </div>
                                )}

                                {/* 가입하기 버튼 (form 하단, '다음'과 동일 위치/스타일) */}
                                <button
                                    style={{
                                        width: "100%",
                                        padding: "14px 0",
                                        borderRadius: "12px",
                                        border: "none",
                                        background: "#ff6a63",
                                        color: "#fff",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        marginTop: "8px"
                                    }}
                                    onClick={handleSignupSubmit}
                                >
                                    기본정보 입력
                                </button>

                                {/* 닫기 텍스트 (하단) */}
                                <div className="google-bottom-area" style={{ paddingBottom: "0" }}>
                                    <span
                                        className="login-close-text"
                                        onClick={() => {
                                            // RN 환경이면 RN 로그인 화면으로 이동
                                            if (handleCloseToNativeLogin()) {
                                                return;
                                            }
                                            setSignupStep(null);
                                            setError("");
                                            setSignupForm({ name: null, phone: "", birthdayYear: "", birthdayMonth: "", birthdayDay: "", message: "" });
                                            setUserStatus(null);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        닫기
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* 기본 구글 로그인 화면 */}
                            <div className="google-main google-main-large">
                                {signupStep === "confirm" ? (
                                    <>
                                        {/* 회원가입 확인 안내 */}
                                        <img
                                            src="/img/google_logo.png"
                                            className="google-circle-icon"
                                            style={{ marginBottom: 10 }}
                                            alt="Google"
                                        />
                                        <div className="google-center-text">
                                            <span>
                                                구글 인증에 성공했습니다.
                                                <br />
                                                myCert를 이용하시려면 본인확인을 해주세요.
                                            </span>
                                        </div>
                                        {/* 기본정보 입력, 관리자 문의 버튼 (텍스트 아래) */}
                                        <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "12px", width: "100%", padding: "0 24px" }}>
                                            <button
                                                className="google-signin-btn"
                                                onClick={() => {
                                                    setSignupStep("verify");
                                                }}
                                            >
                                                <span className="google-signin-text">본인확인하기</span>
                                            </button>
                                            {/* <button
                                                className="google-bottom-btn"
                                                onClick={() => {
                                                    setShowAdminContactModal(true);
                                                }}
                                            >
                                                관리자 문의
                                            </button> */}
                                        </div>
                                        {/* 닫기 텍스트 (하단) */}
                                        <div className="google-bottom-area" style={{ paddingBottom: "0", paddingTop: "0" }}>
                                            <span
                                                className="login-close-text"
                                                onClick={() => {
                                                    // RN 환경이면 RN 로그인 화면으로 이동
                                                    if (handleCloseToNativeLogin()) {
                                                        return;
                                                    }
                                                    setSignupStep(null);
                                                    setError("");
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                닫기
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* 기본 구글 로그인 안내 */}
                                        <img
                                            src="/img/google_logo.png"
                                            className="google-circle-icon"
                                            style={{ marginBottom: 10 }}
                                            alt="Google"
                                        />
                                        <div className="google-center-text">
                                            <span>
                                                {isApp
                                                    ? "로그인 정보를 확인 중입니다..."
                                                    : "myCert를 이용하시려면\n구글 계정으로 로그인하세요."}
                                            </span>
                                        </div>
                                    </>
                                )}
                                {debugInfo && !signupStep && (
                                    <div
                                        style={{
                                            marginTop: 20,
                                            padding: 10,
                                            backgroundColor: "#f0f0f0",
                                            fontSize: 10,
                                            textAlign: "left",
                                            borderRadius: 4,
                                            maxHeight: 150,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {debugInfo}
                                    </div>
                                )}

                                {!isApp && !signupStep && (
                                    <div className="google-bottom-area" style={{ paddingBottom: "0" }}>
                                        <button className="google-signin-btn" onClick={handleGoogleAuth}>
                                            <svg className="google-signin-icon" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                                <g fill="none" fillRule="evenodd">
                                                    <path d="M17.64 9.2045c0-.6371-.0573-1.2516-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.6149z" fill="#4285F4" />
                                                    <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853" />
                                                    <path d="M3.9636 10.71c-.18-.54-.2822-1.1173-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.9636 10.71z" fill="#FBBC05" />
                                                    <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.3459l2.5813-2.5814C13.4632.8918 11.4264 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9636 7.29C4.6714 5.1627 6.6556 3.5795 9 3.5795z" fill="#EA4335" />
                                                </g>
                                            </svg>
                                            <span className="google-signin-text">
                                                {devMode ? "구글 로그인" : "구글 로그인"}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>

                        </>
                    )}
                </>
            ) : (
                <div className="login-main">
                    {userEmail && (
                        <div className="login-user-card">
                            <div className="login-user-name">
                                {userName || "이름 미지정"}
                            </div>
                            <div className="login-user-email">{userEmail}</div>
                        </div>
                    )}
                </div>
            )}

            {/* 관리자 문의하기 모달 */}
            {showAdminContactModal && (
                <div
                    className="setting-modal-overlay"
                    onClick={() => setShowAdminContactModal(false)}
                >
                    <div
                        className="setting-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "400px", width: "90%" }}
                    >
                        <div className="setting-modal-bar">
                            <b>관리자 문의</b>
                        </div>

                        <div className="setting-modal-body" style={{ padding: "24px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "#333",
                                    padding: "12px 16px",
                                    background: "#f9f9f9",
                                    borderRadius: "8px",
                                    gap: "12px"
                                }}
                            >
                                <span
                                    style={{
                                        flex: 1,
                                        userSelect: "all",
                                        cursor: "text"
                                    }}
                                    onClick={(e) => {
                                        const range = document.createRange();
                                        range.selectNodeContents(e.target);
                                        const selection = window.getSelection();
                                        selection.removeAllRanges();
                                        selection.addRange(range);
                                    }}
                                >
                                    {adminPhoneNumber}
                                </span>
                                <button
                                    style={{
                                        padding: "8px",
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#666",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}
                                    onClick={() => {
                                        // 하이픈 제거하고 숫자만 복사
                                        const phoneNumberOnly = adminPhoneNumber.replace(/-/g, '');
                                        if (navigator.clipboard && navigator.clipboard.writeText) {
                                            navigator.clipboard.writeText(phoneNumberOnly).then(() => {
                                                alert('전화번호가 복사되었습니다.');
                                            }).catch(() => {
                                                // 폴백: 텍스트 선택
                                                const textArea = document.createElement('textarea');
                                                textArea.value = phoneNumberOnly;
                                                document.body.appendChild(textArea);
                                                textArea.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(textArea);
                                                alert('전화번호가 복사되었습니다.');
                                            });
                                        } else {
                                            // 구형 브라우저 폴백
                                            const textArea = document.createElement('textarea');
                                            textArea.value = phoneNumberOnly;
                                            document.body.appendChild(textArea);
                                            textArea.select();
                                            document.execCommand('copy');
                                            document.body.removeChild(textArea);
                                            alert('전화번호가 복사되었습니다.');
                                        }
                                    }}
                                    title="복사하기"
                                >
                                    <FontAwesomeIcon icon={faCopy} style={{ fontSize: "18px" }} />
                                </button>
                            </div>
                        </div>

                        <div className="setting-modal-actions" style={{ display: "flex", gap: "8px", flexDirection: (isMobileDevice() || isAppEnvironment()) ? "row" : "column" }}>
                            {(isMobileDevice() || isAppEnvironment()) && (
                                <a
                                    href={`tel:${adminPhoneNumber.replace(/-/g, '')}`}
                                    className="setting-btn"
                                    style={{ flex: 1, textDecoration: "none", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                    전화하기
                                </a>
                            )}
                            <button
                                className="setting-btn outline"
                                style={{ flex: 1 }}
                                onClick={() => setShowAdminContactModal(false)}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 로딩 스피너 오버레이 */}
            {loading && (
                <div className="login-loading-overlay">
                    <div className="login-loading-spinner" />
                    <div className="login-loading-text">
                        {loadingMessage || "처리 중입니다..."}
                    </div>
                </div>
            )}

        </div>
    );
}
